import * as React from 'react';
import equal from 'fast-deep-equal/react';
import { Player, PlayerState } from 'video-react';
import styles from './VideoPlayer.scss';
import 'video-react/dist/video-react.css';
import { useSelector, useDispatch } from 'react-redux';
import { State } from '~/Reducers';
import { finishVideo } from '~/Actions/ActionApp';
import { VIDEO_MAP, VIDEO_NAMES } from '~/components/VideoPlayer/videoMap';
import Cron from '~/utils/Cron';
import History from '~/utils/history';
import { isInGameBrowser } from '~/utils/utils';
import VideoSkipLabel from '~/components/VideoPlayer/VideoSkipLabel';

interface IVideoPlayerState {
    isStartedVideo: boolean;
    currentVideoName: VIDEO_NAMES;
    currentVideoUrl: string;
    fadeOutVideoInSeconds: number;
    onFinishVideoCallback: () => void;
    isTrusted: boolean;
    volume: number;
}

const stateSelector = (state: State): IVideoPlayerState => {
    return {
        isStartedVideo: state.ReducerApp.isStartedVideo,
        currentVideoName: state.ReducerApp.currentVideoName,
        currentVideoUrl: state.ReducerApp.currentVideoUrl,
        onFinishVideoCallback: state.ReducerApp.onFinishVideoCallback,
        isTrusted: state.ReducerApp.isTrusted,
        fadeOutVideoInSeconds: state.ReducerApp.fadeOutVideoInSeconds,
        volume: state.ReducerApp.volume,
    };
};

const VideoPlayer = () => {
    const dispatch = useDispatch();
    const appState = useSelector<State, IVideoPlayerState>(stateSelector, equal);
    const [player, setPlayer] = React.useState(null);
    const ref: React.RefObject<HTMLDivElement> = React.useRef(null);

    const finish = () => {
        dispatch(finishVideo());
        appState.onFinishVideoCallback?.();
    };

    const handleStateChange = (state: PlayerState) => {
        if (appState.fadeOutVideoInSeconds && state.currentTime >= state.duration - appState.fadeOutVideoInSeconds) {
            ref?.current?.classList.remove(styles.active);
        }
        if (state.ended) {
            ref?.current?.classList.remove(styles.active);
            Cron.newDeferredTask(
                'finish-video',
                () => {
                    finish();
                },
                300,
            );
        }
    };

    React.useEffect(() => {
        appState.isStartedVideo &&
            setTimeout(() => {
                ref?.current?.classList.add(styles.active);
                if (!isInGameBrowser && player && History.getHistory()?.length <= 1) {
                    try {
                        player.muted = !appState.isTrusted;
                    } catch (e) {
                        // autoplay police
                    }
                }
                player?.play();
                player?.subscribeToStateChange(handleStateChange.bind(this));
            }, 200);
    });

    if (!appState.currentVideoName && !appState.currentVideoUrl) {
        return null;
    }

    return (
        <div className={styles.wrapper} ref={ref}>
            <div className={styles.player}>
                <Player
                    ref={(player: any) => {
                        setPlayer(player);
                    }}
                    volume={appState.volume || 100}
                    autoPlay={appState.isStartedVideo}
                    fluid
                >
                    <source src={appState.currentVideoName && appState.currentVideoName in VIDEO_MAP ? VIDEO_MAP[appState.currentVideoName] : appState.currentVideoUrl} type="video/mp4" />
                </Player>
                <VideoSkipLabel callback={finish} />
            </div>
        </div>
    );
};

export default VideoPlayer;
